import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowDownToLine,
  Download,
  LogOut,
  RefreshCw,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  Shield,
  SlidersHorizontal,
  UserRoundSearch,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import { adminAPI } from '../services/adminAPI';
import PWAInstallBanner from '../components/PWAInstallBanner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  getWithdrawalExpiry,
  getWithdrawalStatusPresentation,
  isAtlasWithdrawal,
  isWithdrawalCodeExpired,
} from '../utils/withdrawalPresentation';
import {
  getCashPickupActivityLabel,
  getCashPickupStatusPresentation,
  isAtlasCashPickup,
} from '../utils/cashPickupPresentation';

const REPORT_TABS = ['users', 'transactions', 'topups', 'withdrawals', 'collections', 'caddie-links', 'audit-events', 'activity-timelines'];
const TABLE_PAGE_SIZE = '25';

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  const sign = amount < 0 ? '-' : '';
  const [whole, decimals] = Math.abs(amount).toFixed(2).split('.');
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `R${sign}${groupedWhole}.${decimals}`;
};
const formatCurrencyOrDash = (value) => (Number(value || 0) ? formatCurrency(value) : '-');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');

const getDefaultRange = (days = 30) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));

  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
};

const buildOverviewFilters = () => ({
  ...getDefaultRange(30),
  days: '30',
});

const buildReportFilters = () => ({
  ...getDefaultRange(30),
  page: '1',
  page_size: TABLE_PAGE_SIZE,
  search: '',
  status: '',
  min_amount: '',
  max_amount: '',
  user_type: '',
  transaction_type: '',
  provider: '',
  retailer: '',
  mode: '',
  category: '',
  severity: '',
  actor_type: '',
  subject_type: '',
  aml_only: '',
  scope: 'any',
});

const buildInitialReportFilters = () => ({
  users: buildReportFilters(),
  transactions: buildReportFilters(),
  topups: buildReportFilters(),
  withdrawals: buildReportFilters(),
  collections: buildReportFilters(),
  'caddie-links': buildReportFilters(),
  'audit-events': buildReportFilters(),
  'activity-timelines': buildReportFilters(),
});

const REPORT_LABELS = {
  users: 'Users',
  transactions: 'Transactions',
  topups: 'Wallet Top-ups',
  withdrawals: 'Withdrawals',
  collections: 'Cash Collections',
  'caddie-links': 'Caddie Links',
  'audit-events': 'Audit Events',
  'activity-timelines': 'Activity Timelines',
};

const REPORT_TABLE_HEAD_CLASS = 'bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-700';
const formatAuditList = (values) => (Array.isArray(values) && values.length ? values.join(', ') : '-');
const createWithdrawalCollectionKey = (referenceCode) => {
  const uniquePart = globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `withdrawal-collection-${referenceCode}-${uniquePart}`;
};

function SummaryCard({ title, value, subtitle, tone = 'emerald' }) {
  const toneClasses = {
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-900',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-900',
    blue: 'from-blue-50 to-white border-blue-200 text-blue-900',
    slate: 'from-slate-100 to-white border-slate-200 text-slate-900',
  };

  return (
    <Card className={`wallet-card bg-gradient-to-br ${toneClasses[tone] || toneClasses.emerald}`}>
      <CardHeader className="pb-3">
        <CardDescription className="text-slate-600">{title}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-bold">{value}</p>
        {subtitle ? <p className="mt-2 text-sm text-slate-600">{subtitle}</p> : null}
      </CardContent>
    </Card>
  );
}

function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <Select value={value || 'all'} onValueChange={(nextValue) => onChange(nextValue === 'all' ? '' : nextValue)}>
      <SelectTrigger className="bg-white">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{label}</p>
      {children}
    </div>
  );
}

function ReportToolbar({
  tabName,
  filters,
  loading,
  onFilterChange,
  onApply,
  onReset,
  onExportCsv,
  onExportPdf,
}) {
  const showSearch = ['users', 'transactions', 'collections', 'caddie-links'].includes(tabName);
  const showAmountRange = ['transactions', 'topups', 'withdrawals', 'collections'].includes(tabName);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <FilterField label="From Date">
          <Input
            type="date"
            value={filters.from}
            onChange={(event) => onFilterChange('from', event.target.value)}
          />
        </FilterField>
        <FilterField label="To Date">
          <Input
            type="date"
            value={filters.to}
            onChange={(event) => onFilterChange('to', event.target.value)}
          />
        </FilterField>

        {tabName === 'users' ? (
          <FilterField label="User Type">
            <FilterSelect
              value={filters.user_type}
              onChange={(value) => onFilterChange('user_type', value)}
              placeholder="All user types"
              options={[
                { value: 'golfer', label: 'Golfers' },
                { value: 'caddie', label: 'Caddies' },
                { value: 'admin', label: 'Admins' },
              ]}
            />
          </FilterField>
        ) : null}

        {tabName === 'transactions' ? (
          <FilterField label="Transaction Type">
            <FilterSelect
              value={filters.transaction_type}
              onChange={(value) => onFilterChange('transaction_type', value)}
              placeholder="All transaction types"
              options={[
                { value: 'wallet_reload', label: 'Wallet reload' },
                { value: 'send_money', label: 'Send money' },
                { value: 'withdrawal', label: 'Withdrawal' },
              ]}
            />
          </FilterField>
        ) : null}

        {tabName === 'collections' ? (
          <>
            <FilterField label="Provider">
              <FilterSelect
                value={filters.provider}
                onChange={(value) => onFilterChange('provider', value)}
                placeholder="All providers"
                options={[
                  { value: 'caddiepay', label: 'CaddiePay' },
                  { value: 'atlas', label: 'Atlas' },
                ]}
              />
            </FilterField>
            <FilterField label="Retailer">
              <Input
                value={filters.retailer}
                onChange={(event) => onFilterChange('retailer', event.target.value)}
                placeholder="Legacy collections only"
              />
            </FilterField>
          </>
        ) : null}

        {tabName === 'caddie-links' ? (
          <FilterField label="Link Mode">
            <FilterSelect
              value={filters.mode}
              onChange={(value) => onFilterChange('mode', value)}
              placeholder="All link modes"
              options={[
                { value: 'linked', label: 'Linked users' },
                { value: 'manual', label: 'Manual entries' },
              ]}
            />
          </FilterField>
        ) : null}

        {['users', 'transactions', 'topups', 'withdrawals', 'collections'].includes(tabName) ? (
          <FilterField label="Status">
            <FilterSelect
              value={filters.status}
              onChange={(value) => onFilterChange('status', value)}
              placeholder="All statuses"
              options={[
                { value: 'active', label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
                { value: 'pending', label: 'Pending' },
                { value: 'completed', label: 'Completed' },
                { value: 'complete', label: 'Complete' },
                { value: 'failed', label: 'Failed' },
                { value: 'collected', label: 'Collected' },
                ...(['withdrawals', 'collections'].includes(tabName) ? [
                  { value: 'pending_issuance', label: 'Preparing Atlas voucher' },
                  { value: 'voucher_issued', label: 'Atlas voucher issued' },
                  { value: 'redemption_code_minted', label: 'Atlas code minted' },
                  { value: 'redemption_code_delivered', label: 'Atlas code sent' },
                  { value: 'issuance_failed', label: 'Atlas issuance refunded' },
                ] : []),
              ]}
            />
          </FilterField>
        ) : null}

        {showSearch ? (
          <FilterField label="Search">
            <Input
              value={filters.search}
              onChange={(event) => onFilterChange('search', event.target.value)}
              placeholder="Name, email, phone, or reference"
            />
          </FilterField>
        ) : null}

        {showAmountRange ? (
          <FilterField label="Minimum Amount">
            <Input
              value={filters.min_amount}
              onChange={(event) => onFilterChange('min_amount', event.target.value)}
              placeholder="e.g. 100"
              inputMode="decimal"
            />
          </FilterField>
        ) : null}

        {showAmountRange ? (
          <FilterField label="Maximum Amount">
            <Input
              value={filters.max_amount}
              onChange={(event) => onFilterChange('max_amount', event.target.value)}
              placeholder="e.g. 10000"
              inputMode="decimal"
            />
          </FilterField>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          onClick={onApply}
          className="bg-emerald-900 text-white hover:bg-emerald-800"
          disabled={loading}
        >
          {loading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
          Apply Filters
        </Button>
        <Button variant="outline" onClick={onReset} disabled={loading}>
          Reset
        </Button>
        <Button variant="outline" onClick={onExportCsv} disabled={loading}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
        <Button variant="outline" onClick={onExportPdf} disabled={loading}>
          <ArrowDownToLine className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>
    </div>
  );
}

function PaginationBar({ pagination, loading, onPageChange }) {
  if (!pagination) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
      <p className="text-sm text-slate-600">
        Page {pagination.page} of {pagination.total_pages} | {pagination.total_count} records
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={loading || pagination.page <= 1}
          onClick={() => onPageChange(pagination.page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={loading || pagination.page >= pagination.total_pages}
          onClick={() => onPageChange(pagination.page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function AuditToolbar({
  title,
  filters,
  loading,
  onFilterChange,
  onApply,
  onReset,
  onExportCsv,
  onExportPdf,
  includeScope = false,
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <FilterField label="From Date">
          <Input type="date" value={filters.from} onChange={(event) => onFilterChange('from', event.target.value)} />
        </FilterField>
        <FilterField label="To Date">
          <Input type="date" value={filters.to} onChange={(event) => onFilterChange('to', event.target.value)} />
        </FilterField>
        <FilterField label="Search">
          <Input
            value={filters.search}
            onChange={(event) => onFilterChange('search', event.target.value)}
            placeholder={title}
          />
        </FilterField>
        <FilterField label="Category">
          <FilterSelect
            value={filters.category}
            onChange={(value) => onFilterChange('category', value)}
            placeholder="All categories"
            options={[
              { value: 'onboarding', label: 'Onboarding' },
              { value: 'security', label: 'Security' },
              { value: 'profile', label: 'Profile' },
              { value: 'admin', label: 'Admin' },
              { value: 'wallet', label: 'Wallet' },
              { value: 'payment', label: 'Payment' },
              { value: 'withdrawal', label: 'Withdrawal' },
              { value: 'collection', label: 'Collection' },
              { value: 'caddie_management', label: 'Caddie Management' },
            ]}
          />
        </FilterField>
        <FilterField label="Severity">
          <FilterSelect
            value={filters.severity}
            onChange={(value) => onFilterChange('severity', value)}
            placeholder="All severities"
            options={[
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
            ]}
          />
        </FilterField>
        <FilterField label={includeScope ? 'Timeline Scope' : 'Actor Type'}>
          <FilterSelect
            value={includeScope ? filters.scope : filters.actor_type}
            onChange={(value) => onFilterChange(includeScope ? 'scope' : 'actor_type', value)}
            placeholder={includeScope ? 'Any role' : 'All actor types'}
            options={includeScope ? [
              { value: 'any', label: 'Any role' },
              { value: 'actor', label: 'Actor only' },
              { value: 'subject', label: 'Subject only' },
            ] : [
              { value: 'golfer', label: 'Golfer actor' },
              { value: 'caddie', label: 'Caddie actor' },
              { value: 'admin', label: 'Admin actor' },
            ]}
          />
        </FilterField>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-3 xl:grid-cols-4">
        {!includeScope ? (
          <FilterField label="Subject Type">
            <FilterSelect
              value={filters.subject_type}
              onChange={(value) => onFilterChange('subject_type', value)}
              placeholder="All subject types"
              options={[
                { value: 'golfer', label: 'Golfer subject' },
                { value: 'caddie', label: 'Caddie subject' },
                { value: 'admin', label: 'Admin subject' },
              ]}
            />
          </FilterField>
        ) : null}
        <FilterField label="Status">
          <FilterSelect
            value={filters.status}
            onChange={(value) => onFilterChange('status', value)}
            placeholder="All statuses"
            options={[
              { value: 'success', label: 'Success' },
              { value: 'failed', label: 'Failed' },
              { value: 'blocked', label: 'Blocked' },
              { value: 'not_found', label: 'Not found' },
            ]}
          />
        </FilterField>
        {!includeScope ? (
          <FilterField label="AML Scope">
            <FilterSelect
              value={filters.aml_only}
              onChange={(value) => onFilterChange('aml_only', value)}
              placeholder="All audit events"
              options={[
                { value: 'true', label: 'AML relevant only' },
              ]}
            />
          </FilterField>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={onApply} className="bg-emerald-900 text-white hover:bg-emerald-800" disabled={loading}>
          {loading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
          Apply Filters
        </Button>
        <Button variant="outline" onClick={onReset} disabled={loading}>Reset</Button>
        <Button variant="outline" onClick={onExportCsv} disabled={loading}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
        <Button variant="outline" onClick={onExportPdf} disabled={loading}>
          <ArrowDownToLine className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [bootLoading, setBootLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState({
    users: false,
    transactions: false,
    topups: false,
    withdrawals: false,
    collections: false,
    'caddie-links': false,
    'audit-events': false,
    'activity-timelines': false,
  });
  const [overview, setOverview] = useState(null);
  const [reports, setReports] = useState({
    users: null,
    transactions: null,
    topups: null,
    withdrawals: null,
    collections: null,
    'caddie-links': null,
    'audit-events': null,
    'activity-timelines': null,
  });
  const [overviewFilters, setOverviewFilters] = useState(buildOverviewFilters());
  const [reportFilters, setReportFilters] = useState(buildInitialReportFilters());
  const [withdrawalReferenceCode, setWithdrawalReferenceCode] = useState('');
  const [verifiedWithdrawal, setVerifiedWithdrawal] = useState(null);
  const [withdrawalCollectionKey, setWithdrawalCollectionKey] = useState('');
  const [withdrawalActionLoading, setWithdrawalActionLoading] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({
    denominations: [50, 100, 150, 200],
    recommended_amount: 100,
    allow_custom_amount: true,
  });
  const [denominationsInput, setDenominationsInput] = useState('50, 100, 150, 200');
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false);

  // The initial admin bootstrap should only run once after auth guard passes.
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      navigate('/login');
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.user_type !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }

    setUser(userData);

    const hydrate = async () => {
      setBootLoading(true);
      try {
        await Promise.all([
          loadOverview(buildOverviewFilters()),
          loadReport('users', buildInitialReportFilters().users),
          loadPaymentSettings(),
        ]);
      } finally {
        setBootLoading(false);
      }
    };

    hydrate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Report tabs are lazy-loaded the first time an admin opens them.
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'payment-settings' || reports[activeTab]) {
      return;
    }
    loadReport(activeTab, reportFilters[activeTab]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadOverview = async (filters = overviewFilters) => {
    setOverviewLoading(true);
    try {
      const response = await adminAPI.getOverview(filters);
      setOverview(response.data);
    } catch (error) {
      toast.error('Failed to load overview');
      console.error(error);
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    setPaymentSettingsLoading(true);
    try {
      const response = await adminAPI.getPaymentSettings();
      setPaymentSettings(response.data);
      setDenominationsInput(response.data.denominations.join(', '));
    } catch (error) {
      toast.error('Failed to load payment settings');
      console.error(error);
    } finally {
      setPaymentSettingsLoading(false);
    }
  };

  const savePaymentSettings = async () => {
    const denominations = denominationsInput
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value));
    if (!denominations.length || denominations.some((value) => value <= 0)) {
      toast.error('Enter at least one valid positive denomination');
      return;
    }
    if (!denominations.includes(Number(paymentSettings.recommended_amount))) {
      toast.error('The recommended amount must be one of the denominations');
      return;
    }

    setPaymentSettingsLoading(true);
    try {
      const response = await adminAPI.updatePaymentSettings({
        ...paymentSettings,
        denominations,
        recommended_amount: Number(paymentSettings.recommended_amount),
      });
      setPaymentSettings(response.data);
      setDenominationsInput(response.data.denominations.join(', '));
      toast.success('Payment settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment settings');
    } finally {
      setPaymentSettingsLoading(false);
    }
  };

  const loadReport = async (tabName, filters = reportFilters[tabName]) => {
    setReportLoading((current) => ({ ...current, [tabName]: true }));
    try {
      const response = await adminAPI.getReport(tabName, filters);
      setReports((current) => ({ ...current, [tabName]: response.data }));
    } catch (error) {
      toast.error(`Failed to load ${REPORT_LABELS[tabName].toLowerCase()}`);
      console.error(error);
    } finally {
      setReportLoading((current) => ({ ...current, [tabName]: false }));
    }
  };

  const handleOverviewFilterChange = (key, value) => {
    setOverviewFilters((current) => ({ ...current, [key]: value }));
  };

  const handleReportFilterChange = (tabName, key, value) => {
    setReportFilters((current) => ({
      ...current,
      [tabName]: {
        ...current[tabName],
        [key]: value,
      },
    }));
  };

  const applyOverviewFilters = () => {
    loadOverview(overviewFilters);
  };

  const resetOverviewFilters = () => {
    const nextFilters = buildOverviewFilters();
    setOverviewFilters(nextFilters);
    loadOverview(nextFilters);
  };

  const applyReportFilters = (tabName) => {
    const nextFilters = {
      ...reportFilters[tabName],
      page: '1',
    };
    setReportFilters((current) => ({ ...current, [tabName]: nextFilters }));
    loadReport(tabName, nextFilters);
  };

  const resetReportFilters = (tabName) => {
    const nextFilters = buildReportFilters();
    setReportFilters((current) => ({ ...current, [tabName]: nextFilters }));
    loadReport(tabName, nextFilters);
  };

  const handlePageChange = (tabName, nextPage) => {
    const nextFilters = {
      ...reportFilters[tabName],
      page: String(nextPage),
    };
    setReportFilters((current) => ({ ...current, [tabName]: nextFilters }));
    loadReport(tabName, nextFilters);
  };

  const exportReport = async (tabName, format = 'csv') => {
    try {
      const response = await adminAPI.exportReport(tabName, reportFilters[tabName], format);
      const mimeType = format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8;';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tabName}-report.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${REPORT_LABELS[tabName]} ${format.toUpperCase()} export started`);
    } catch (error) {
      toast.error('Failed to export report');
      console.error(error);
    }
  };

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      await adminAPI.updateUserStatus(userId, status);
      toast.success(`User ${status}`);
      loadReport('users', reportFilters.users);
      loadOverview(overviewFilters);
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleVerifyWithdrawal = async () => {
    const referenceCode = withdrawalReferenceCode.trim().toUpperCase();
    if (!referenceCode) {
      toast.error('Enter a withdrawal reference');
      return;
    }

    setWithdrawalActionLoading(true);
    setVerifiedWithdrawal(null);
    try {
      const response = await adminAPI.verifyWithdrawalReference(referenceCode);
      setWithdrawalReferenceCode(referenceCode);
      setVerifiedWithdrawal(response.data);
      setWithdrawalCollectionKey(createWithdrawalCollectionKey(referenceCode));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal reference could not be verified');
    } finally {
      setWithdrawalActionLoading(false);
    }
  };

  const handleCollectWithdrawal = async () => {
    if (!verifiedWithdrawal || !withdrawalCollectionKey) {
      return;
    }
    const confirmed = window.confirm(
      `Confirm that ${formatCurrency(verifiedWithdrawal.amount)} cash has been paid to ${verifiedWithdrawal.caddie_name}. This cannot be reversed.`
    );
    if (!confirmed) {
      return;
    }

    setWithdrawalActionLoading(true);
    try {
      const response = await adminAPI.collectWithdrawalReference(
        verifiedWithdrawal.reference_code,
        withdrawalCollectionKey
      );
      setVerifiedWithdrawal((current) => ({
        ...current,
        status: 'collected',
        collected_at: response.data.collected_at,
      }));
      toast.success(response.data.replayed ? 'Withdrawal was already recorded as collected' : 'Withdrawal collection recorded');
      loadReport('withdrawals', reportFilters.withdrawals);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record withdrawal collection');
    } finally {
      setWithdrawalActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const renderOverview = () => {
    if (!overview) {
      return null;
    }

    const kpis = overview.kpis || {};
    const breakdowns = overview.breakdowns || {};
    const topupStatuses = breakdowns.topup_statuses || [];
    const transactionTypes = breakdowns.transaction_types || [];

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-4">
            <FilterField label="From Date">
              <Input
                type="date"
                value={overviewFilters.from}
                onChange={(event) => handleOverviewFilterChange('from', event.target.value)}
              />
            </FilterField>
            <FilterField label="To Date">
              <Input
                type="date"
                value={overviewFilters.to}
                onChange={(event) => handleOverviewFilterChange('to', event.target.value)}
              />
            </FilterField>
            <Button
              onClick={applyOverviewFilters}
              className="bg-emerald-900 text-white hover:bg-emerald-800"
              disabled={overviewLoading}
            >
              {overviewLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
              Refresh Overview
            </Button>
            <Button variant="outline" onClick={resetOverviewFilters} disabled={overviewLoading}>
              Reset Range
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            title="Users"
            value={kpis.total_users || 0}
            subtitle={`${kpis.active_users || 0} active | ${kpis.suspended_users || 0} suspended`}
            tone="emerald"
          />
          <SummaryCard
            title="Wallet Balance"
            value={formatCurrency(kpis.total_wallet_balance)}
            subtitle="Current wallet liability"
            tone="amber"
          />
          <SummaryCard
            title="Transactions In Range"
            value={kpis.period_transactions || 0}
            subtitle={formatCurrency(kpis.period_transaction_volume)}
            tone="blue"
          />
          <SummaryCard
            title="Completed Top-ups"
            value={kpis.period_completed_topups || 0}
            subtitle={formatCurrency(kpis.period_completed_topup_amount)}
            tone="slate"
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-3">
          <Card className="wallet-card xl:col-span-2">
            <CardHeader>
              <CardTitle>Daily Platform Activity</CardTitle>
              <CardDescription className="text-slate-500">Registrations, transaction counts, and send/top-up movement in the selected period</CardDescription>
            </CardHeader>
            <CardContent className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={overview.daily_activity || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="new_users" stroke="#047857" strokeWidth={2} />
                  <Line type="monotone" dataKey="transaction_count" stroke="#1d4ed8" strokeWidth={2} />
                  <Line type="monotone" dataKey="collection_count" stroke="#b45309" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="wallet-card">
            <CardHeader>
              <CardTitle>Top-up Status Mix</CardTitle>
              <CardDescription className="text-slate-500">PayFast payment lifecycle in the selected period</CardDescription>
            </CardHeader>
            <CardContent className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topupStatuses}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0f766e" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <Card className="wallet-card">
            <CardHeader>
              <CardTitle>Transaction Type Breakdown</CardTitle>
              <CardDescription className="text-slate-500">Volume and usage by financial flow</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {transactionTypes.map((row) => (
                  <div key={row.label} className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-semibold capitalize text-slate-900">{row.label.replaceAll('_', ' ')}</p>
                      <p className="text-sm text-slate-500">{row.count} transactions</p>
                    </div>
                    <p className="font-bold text-emerald-900">{formatCurrency(row.amount)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="wallet-card">
            <CardHeader>
              <CardTitle>Operational Snapshot</CardTitle>
              <CardDescription className="text-slate-500">What changed during the selected reporting window</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm text-slate-500">New users</p>
                <p className="text-2xl font-bold text-slate-900">{kpis.period_new_users || 0}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm text-slate-500">Transaction volume</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(kpis.period_transaction_volume)}</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-sm text-slate-500">Completed top-up amount</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(kpis.period_completed_topup_amount)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderPaymentSettings = () => (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-emerald-950">
          <SlidersHorizontal className="h-5 w-5" />
          Golfer payment amounts
        </CardTitle>
        <CardDescription>
          Configure the amount choices shown when a golfer pays a caddie. These settings apply to wallet and cash-reference payments.
        </CardDescription>
      </CardHeader>
      <CardContent className="max-w-2xl space-y-6">
        <div className="space-y-2">
          <Label htmlFor="payment-denominations">Denominations</Label>
          <Input
            id="payment-denominations"
            value={denominationsInput}
            onChange={(event) => setDenominationsInput(event.target.value)}
            placeholder="50, 100, 150, 200"
            disabled={paymentSettingsLoading}
          />
          <p className="text-sm text-slate-500">Enter rand amounts separated by commas.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="recommended-payment-amount">Recommended amount</Label>
          <Select
            value={String(paymentSettings.recommended_amount)}
            onValueChange={(value) => setPaymentSettings((current) => ({
              ...current,
              recommended_amount: Number(value),
            }))}
          >
            <SelectTrigger id="recommended-payment-amount" className="bg-white">
              <SelectValue placeholder="Select the recommended amount" />
            </SelectTrigger>
            <SelectContent>
              {denominationsInput.split(',').map((value) => Number(value.trim()))
                .filter((value, index, values) => Number.isFinite(value) && value > 0 && values.indexOf(value) === index)
                .map((value) => (
                  <SelectItem key={value} value={String(value)}>R{value}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-500">This option is highlighted for golfers but remains their choice.</p>
        </div>

        <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
          <div className="pr-4">
            <Label htmlFor="allow-custom-payment">Allow a custom amount</Label>
            <p className="mt-1 text-sm text-slate-500">When disabled, golfers must select one of the configured denominations.</p>
          </div>
          <Switch
            id="allow-custom-payment"
            checked={paymentSettings.allow_custom_amount}
            onCheckedChange={(checked) => setPaymentSettings((current) => ({
              ...current,
              allow_custom_amount: checked,
            }))}
            disabled={paymentSettingsLoading}
          />
        </div>

        <div className="flex gap-3">
          <Button
            onClick={savePaymentSettings}
            className="bg-emerald-900 text-white hover:bg-emerald-800"
            disabled={paymentSettingsLoading}
          >
            {paymentSettingsLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save payment settings
          </Button>
          <Button variant="outline" onClick={loadPaymentSettings} disabled={paymentSettingsLoading}>
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderUsersReport = () => {
    const report = reports.users;
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>User Reporting</CardTitle>
          <CardDescription className="text-slate-500">Registrations, balances, and account status across golfers, caddies, and admins</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ReportToolbar
            tabName="users"
            filters={reportFilters.users}
            loading={reportLoading.users}
            onFilterChange={(key, value) => handleReportFilterChange('users', key, value)}
            onApply={() => applyReportFilters('users')}
            onReset={() => resetReportFilters('users')}
            onExportCsv={() => exportReport('users', 'csv')}
            onExportPdf={() => exportReport('users', 'pdf')}
          />

          <div className="grid gap-4 md:grid-cols-4">
            <SummaryCard title="Users" value={report.summary.total_users || 0} tone="emerald" />
            <SummaryCard title="Active" value={report.summary.active_users || 0} tone="blue" />
            <SummaryCard title="Suspended" value={report.summary.suspended_users || 0} tone="slate" />
            <SummaryCard title="Page Wallets" value={formatCurrency(report.summary.page_wallet_balance)} tone="amber" />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Name</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Type</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Phone</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Wallet</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Registered Caddies</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Created</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.full_name}</p>
                      <p className="text-xs text-slate-500">{row.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{row.user_type}</TableCell>
                  <TableCell className="capitalize">{row.status}</TableCell>
                  <TableCell>{row.phone_number}</TableCell>
                  <TableCell className="font-semibold text-emerald-900">{formatCurrency(row.wallet_balance)}</TableCell>
                  <TableCell>{row.registered_caddie_count || 0}</TableCell>
                  <TableCell>{formatDate(row.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateUserStatus(row.id, row.status === 'active' ? 'suspended' : 'active')}
                    >
                      {row.status === 'active' ? 'Suspend' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading.users}
            onPageChange={(nextPage) => handlePageChange('users', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderTransactionsReport = () => {
    const report = reports.transactions;
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Transaction Reporting</CardTitle>
          <CardDescription className="text-slate-500">Platform transaction history with type, status, and amount filtering</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ReportToolbar
            tabName="transactions"
            filters={reportFilters.transactions}
            loading={reportLoading.transactions}
            onFilterChange={(key, value) => handleReportFilterChange('transactions', key, value)}
            onApply={() => applyReportFilters('transactions')}
            onReset={() => resetReportFilters('transactions')}
            onExportCsv={() => exportReport('transactions', 'csv')}
            onExportPdf={() => exportReport('transactions', 'pdf')}
          />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard title="Transactions" value={report.summary.total_transactions || 0} tone="emerald" />
            <SummaryCard title="Page Amount" value={formatCurrency(report.summary.total_amount)} tone="amber" />
            {(report.summary.types || []).slice(0, 2).map((item) => (
              <SummaryCard
                key={item.label}
                title={item.label.replaceAll('_', ' ')}
                value={item.count}
                subtitle={formatCurrency(item.amount)}
                tone="blue"
              />
            ))}
          </div>

          <Table>
            <TableHeader>
                <TableRow>
                  <TableHead className={REPORT_TABLE_HEAD_CLASS}>Date</TableHead>
                  <TableHead className={REPORT_TABLE_HEAD_CLASS}>Type</TableHead>
                  <TableHead className={REPORT_TABLE_HEAD_CLASS}>From</TableHead>
                  <TableHead className={REPORT_TABLE_HEAD_CLASS}>To</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Amount</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell className="capitalize">
                    <p>{row.transaction_type.replaceAll('_', ' ')}</p>
                    {row.payment_provider_name ? <p className="text-xs text-slate-500">{row.payment_provider_name}</p> : null}
                  </TableCell>
                  <TableCell>{row.sender_name}</TableCell>
                  <TableCell>
                    {row.recipient_via_reference ? (
                      <div>
                        <p className="font-semibold text-slate-900">{row.recipient_name}</p>
                        <p className="text-xs text-slate-500">{row.recipient_phone}</p>
                      </div>
                    ) : (
                      row.recipient_name
                    )}
                  </TableCell>
                  <TableCell className="font-semibold text-emerald-900">{formatCurrency(row.amount)}</TableCell>
                  <TableCell className="capitalize">{row.status}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <p>{row.reference_number}</p>
                    {row.gateway_reference ? <p className="text-slate-500">Gateway: {row.gateway_reference}</p> : null}
                    {row.gateway_transaction_id ? <p className="text-slate-500">Txn: {row.gateway_transaction_id}</p> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading.transactions}
            onPageChange={(nextPage) => handlePageChange('transactions', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderTopupsReport = () => {
    const report = reports.topups;
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Top-up Reporting</CardTitle>
          <CardDescription className="text-slate-500">Gateway initiation status versus wallet credit completion</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ReportToolbar
            tabName="topups"
            filters={reportFilters.topups}
            loading={reportLoading.topups}
            onFilterChange={(key, value) => handleReportFilterChange('topups', key, value)}
            onApply={() => applyReportFilters('topups')}
            onReset={() => resetReportFilters('topups')}
            onExportCsv={() => exportReport('topups', 'csv')}
            onExportPdf={() => exportReport('topups', 'pdf')}
          />

          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard title="Top-ups" value={report.summary.total_topups || 0} tone="emerald" />
            <SummaryCard title="Page Amount" value={formatCurrency(report.summary.page_amount)} tone="amber" />
            <SummaryCard
              title="Status Buckets"
              value={(report.summary.statuses || []).length}
              subtitle="Distinct payment states"
              tone="blue"
            />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Created</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>User</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Provider</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Amount</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Credited</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Wallet Reference</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Gateway Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.payment_id}>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.user_name}</p>
                      <p className="text-xs text-slate-500">{row.user_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>{row.payment_provider_name || '-'}</TableCell>
                  <TableCell className="font-semibold text-emerald-900">{formatCurrency(row.amount)}</TableCell>
                  <TableCell className="capitalize">{row.status}</TableCell>
                  <TableCell>{row.credited ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="font-mono text-xs">{row.credited_reference_number || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <p>{row.gateway_reference || '-'}</p>
                    {row.gateway_transaction_id ? <p className="text-slate-500">Txn: {row.gateway_transaction_id}</p> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading.topups}
            onPageChange={(nextPage) => handlePageChange('topups', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderWithdrawalsReport = () => {
    const report = reports.withdrawals;
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Withdrawal Reporting</CardTitle>
          <CardDescription className="text-slate-500">
            CaddiePay reference and Atlas voucher withdrawal visibility
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <Shield className="mt-0.5 h-5 w-5 text-amber-800" />
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="font-semibold text-amber-950">Redeem a CaddiePay withdrawal reference</h3>
                  <p className="mt-1 text-sm text-amber-800">
                    Verify the caddie and amount first. Mark it collected only after cash has been handed to the caddie.
                  </p>
                  <p className="mt-1 text-xs font-medium text-amber-900">
                    Atlas voucher codes are redeemed through participating Flash merchants and are not processed here.
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={withdrawalReferenceCode}
                    onChange={(event) => {
                      setWithdrawalReferenceCode(event.target.value.toUpperCase());
                      setVerifiedWithdrawal(null);
                      setWithdrawalCollectionKey('');
                    }}
                    placeholder="WD reference code"
                    className="bg-white font-mono uppercase"
                    disabled={withdrawalActionLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleVerifyWithdrawal}
                    disabled={withdrawalActionLoading || !withdrawalReferenceCode.trim()}
                  >
                    {withdrawalActionLoading && !verifiedWithdrawal ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Verify Reference
                  </Button>
                </div>
                {verifiedWithdrawal ? (
                  <div className="rounded-lg border border-amber-200 bg-white p-4">
                    <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <p className="text-slate-500">Caddie</p>
                        <p className="font-semibold text-slate-900">{verifiedWithdrawal.caddie_name}</p>
                        <p className="text-xs text-slate-500">{verifiedWithdrawal.caddie_phone}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Amount</p>
                        <p className="text-xl font-bold text-emerald-900">{formatCurrency(verifiedWithdrawal.amount)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Expires</p>
                        <p className="font-semibold text-slate-900">{formatDateTime(verifiedWithdrawal.expires_at)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Status</p>
                        <p className="font-semibold capitalize text-slate-900">{verifiedWithdrawal.status}</p>
                      </div>
                    </div>
                    {verifiedWithdrawal.status === 'active' ? (
                      <Button
                        type="button"
                        onClick={handleCollectWithdrawal}
                        disabled={withdrawalActionLoading}
                        className="mt-4 bg-amber-800 text-white hover:bg-amber-700"
                      >
                        {withdrawalActionLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Confirm Cash Paid
                      </Button>
                    ) : (
                      <p className="mt-4 text-sm font-semibold text-emerald-800">Collection recorded successfully.</p>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <ReportToolbar
            tabName="withdrawals"
            filters={reportFilters.withdrawals}
            loading={reportLoading.withdrawals}
            onFilterChange={(key, value) => handleReportFilterChange('withdrawals', key, value)}
            onApply={() => applyReportFilters('withdrawals')}
            onReset={() => resetReportFilters('withdrawals')}
            onExportCsv={() => exportReport('withdrawals', 'csv')}
            onExportPdf={() => exportReport('withdrawals', 'pdf')}
          />

          <div className="grid gap-4 md:grid-cols-3">
            <SummaryCard title="Withdrawals" value={report.summary.total_withdrawals || 0} tone="emerald" />
            <SummaryCard title="Page Amount" value={formatCurrency(report.summary.page_amount)} tone="amber" />
            <SummaryCard title="Statuses" value={(report.summary.statuses || []).length} tone="blue" />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Provider / Reference</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Caddie</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Amount</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Created</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Expires</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => {
                const atlasWithdrawal = isAtlasWithdrawal(row);
                const expiry = getWithdrawalExpiry(row);
                const status = getWithdrawalStatusPresentation(
                  row.status,
                  isWithdrawalCodeExpired(row)
                );

                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs">
                      <p className="font-semibold text-slate-900">
                        {atlasWithdrawal ? 'Atlas' : 'CaddiePay'}
                      </p>
                      <p className="mt-1 font-mono text-slate-500">
                        {atlasWithdrawal ? row.atlas_voucher_id || 'Voucher pending' : row.reference_code}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-900">{row.user_name}</p>
                        <p className="text-xs text-slate-500">{row.user_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-emerald-900">{formatCurrency(row.amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={status.className}>
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(row.created_at)}</TableCell>
                    <TableCell>{atlasWithdrawal ? formatDateTime(expiry) : formatDate(expiry)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading.withdrawals}
            onPageChange={(nextPage) => handlePageChange('withdrawals', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderCollectionsReport = () => {
    const report = reports.collections;
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Cash Collection Reporting</CardTitle>
          <CardDescription className="text-slate-500">Track CaddiePay references and Atlas SMS cash pickups for unregistered caddies</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ReportToolbar
            tabName="collections"
            filters={reportFilters.collections}
            loading={reportLoading.collections}
            onFilterChange={(key, value) => handleReportFilterChange('collections', key, value)}
            onApply={() => applyReportFilters('collections')}
            onReset={() => resetReportFilters('collections')}
            onExportCsv={() => exportReport('collections', 'csv')}
            onExportPdf={() => exportReport('collections', 'pdf')}
          />

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm font-semibold text-blue-900">Atlas status confirms voucher issuance and SMS delivery—not redemption or settlement at Flash.</p>
            <p className="mt-1 text-xs text-blue-700">Use Atlas reconciliation data when it becomes available to confirm that a code was redeemed and settled.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <SummaryCard title="Cash Pickups" value={report.summary.total_collections || 0} tone="emerald" />
            <SummaryCard title="Atlas" value={(report.summary.providers || []).find((item) => item.label === 'atlas')?.count || 0} tone="blue" />
            <SummaryCard title="CaddiePay" value={(report.summary.providers || []).find((item) => item.label === 'caddiepay')?.count || 0} tone="slate" />
            <SummaryCard title="Page Amount" value={formatCurrency(report.summary.page_amount)} tone="amber" />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Provider / Reference</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Golfer</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Recipient</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Amount</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Delivery / Collection</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Expires</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => {
                const atlasCashPickup = isAtlasCashPickup(row);
                const status = getCashPickupStatusPresentation(row);

                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs">
                      <p className="font-semibold text-slate-900">{atlasCashPickup ? 'Atlas' : 'CaddiePay'}</p>
                      <p className="mt-1 font-mono text-slate-500">{row.provider_reference || '-'}</p>
                      {atlasCashPickup && row.atlas_transaction_id ? (
                        <p className="mt-1 font-mono text-[11px] text-slate-400">Txn: {row.atlas_transaction_id}</p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-900">{row.sender_name}</p>
                        <p className="text-xs text-slate-500">{row.sender_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-900">{row.recipient_name || 'Not provided'}</p>
                        <p className="text-xs text-slate-500">{row.recipient_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-emerald-900">{formatCurrency(row.amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={status.className}>{status.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-800">{getCashPickupActivityLabel(row)}</p>
                      <p className="mt-1 text-xs text-slate-500">{formatDateTime(row.provider_activity_at)}</p>
                    </TableCell>
                    <TableCell>{formatDateTime(row.provider_expiry)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading.collections}
            onPageChange={(nextPage) => handlePageChange('collections', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderCaddieLinksReport = () => {
    const report = reports['caddie-links'];
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Caddie Relationship Reporting</CardTitle>
          <CardDescription className="text-slate-500">See how golfers are registering and linking caddies across the platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ReportToolbar
            tabName="caddie-links"
            filters={reportFilters['caddie-links']}
            loading={reportLoading['caddie-links']}
            onFilterChange={(key, value) => handleReportFilterChange('caddie-links', key, value)}
            onApply={() => applyReportFilters('caddie-links')}
            onReset={() => resetReportFilters('caddie-links')}
            onExportCsv={() => exportReport('caddie-links', 'csv')}
            onExportPdf={() => exportReport('caddie-links', 'pdf')}
          />

          <div className="grid gap-4 md:grid-cols-4">
            <SummaryCard title="Relationships" value={report.summary.total_links || 0} tone="emerald" />
            <SummaryCard title="Linked Users" value={report.summary.linked_count || 0} tone="blue" />
            <SummaryCard title="Manual Entries" value={report.summary.manual_count || 0} tone="slate" />
            <SummaryCard title="Page Payments" value={formatCurrency(report.summary.page_payments)} tone="amber" />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Golfer</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Caddie</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Mode</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Payments</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Total Paid</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Last Payment</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.golfer_name}</p>
                      <p className="text-xs text-slate-500">{row.golfer_email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{`${row.caddie_name} ${row.caddie_surname}`.trim()}</p>
                      <p className="text-xs text-slate-500">{row.caddie_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>{row.is_linked ? 'Linked account' : 'Manual entry'}</TableCell>
                  <TableCell>{row.payment_count}</TableCell>
                  <TableCell className="font-semibold text-emerald-900">{formatCurrency(row.total_payments)}</TableCell>
                  <TableCell>{formatDate(row.last_payment_at)}</TableCell>
                  <TableCell>{formatDate(row.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading['caddie-links']}
            onPageChange={(nextPage) => handlePageChange('caddie-links', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderAuditEventsReport = () => {
    const report = reports['audit-events'];
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Audit Event Reporting</CardTitle>
          <CardDescription className="text-slate-500">Immutable administrative, onboarding, security, identity, and funds-movement events for AML-oriented oversight</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <AuditToolbar
            title="Search actor, subject, email, phone, reference, or path"
            filters={reportFilters['audit-events']}
            loading={reportLoading['audit-events']}
            onFilterChange={(key, value) => handleReportFilterChange('audit-events', key, value)}
            onApply={() => applyReportFilters('audit-events')}
            onReset={() => resetReportFilters('audit-events')}
            onExportCsv={() => exportReport('audit-events', 'csv')}
            onExportPdf={() => exportReport('audit-events', 'pdf')}
          />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard title="Audit Events" value={report.summary.total_events || 0} tone="emerald" />
            <SummaryCard title="AML Relevant" value={report.summary.aml_relevant_events || 0} tone="amber" />
            <SummaryCard title="High Severity" value={report.summary.high_severity_events || 0} tone="blue" />
            <SummaryCard title="Admin Actions" value={report.summary.admin_actions || 0} subtitle={`${report.summary.failed_actions || 0} failed or blocked`} tone="slate" />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Timestamp</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Event</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Actor</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Subject</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Entity</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>AML / Changes</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Severity</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Amount</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Trace</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.event_type.replaceAll('_', ' ')}</p>
                      <p className="text-xs uppercase tracking-[0.08em] text-slate-500">{row.category}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.actor_name}</p>
                      <p className="text-xs text-slate-500">{row.actor_type}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.subject_name}</p>
                      <p className="text-xs text-slate-500">{row.subject_type}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.entity_type}</p>
                      <p className="text-xs text-slate-500">{row.entity_reference}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-700">{formatAuditList(row.aml_tags)}</p>
                      <p className="mt-1 text-xs text-slate-500">{row.changed_fields_display}</p>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{row.severity}</TableCell>
                  <TableCell className="font-semibold text-emerald-900">{formatCurrencyOrDash(row.amount)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="capitalize">{row.status}</p>
                      <p className="text-xs text-slate-500">{row.event_hash_short || '-'}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {row.payment_provider_name !== '-' ? (
                        <>
                          <p className="text-xs font-semibold">{row.payment_provider_name}: {row.gateway_reference}</p>
                          {row.gateway_transaction_id !== '-' ? <p className="font-mono text-xs text-slate-500">{row.gateway_transaction_id}</p> : null}
                        </>
                      ) : null}
                      <p className="font-mono text-xs">{row.ip_address}</p>
                      <p className="truncate text-xs text-slate-500">{row.request_path}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading['audit-events']}
            onPageChange={(nextPage) => handlePageChange('audit-events', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  const renderActivityTimelinesReport = () => {
    const report = reports['activity-timelines'];
    if (!report) {
      return null;
    }

    return (
      <Card className="wallet-card">
        <CardHeader>
          <CardTitle>Activity Timelines</CardTitle>
          <CardDescription className="text-slate-500">Investigate one user, admin, phone number, email, or reference across onboarding, profile changes, admin actions, and money movement</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <AuditToolbar
            title="Search user/admin name, email, phone, user ID, or reference"
            filters={reportFilters['activity-timelines']}
            loading={reportLoading['activity-timelines']}
            onFilterChange={(key, value) => handleReportFilterChange('activity-timelines', key, value)}
            onApply={() => applyReportFilters('activity-timelines')}
            onReset={() => resetReportFilters('activity-timelines')}
            onExportCsv={() => exportReport('activity-timelines', 'csv')}
            onExportPdf={() => exportReport('activity-timelines', 'pdf')}
            includeScope
          />

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <SummaryCard title="Timeline Events" value={report.summary.total_events || 0} tone="emerald" />
            <SummaryCard title="Matched Users" value={report.summary.matched_users || 0} tone="blue" />
            <SummaryCard title="Admin Events" value={report.summary.admin_events || 0} tone="slate" />
            <SummaryCard title="Security Events" value={report.summary.security_events || 0} tone="amber" />
            <SummaryCard title="Financial Events" value={report.summary.financial_events || 0} tone="blue" />
          </div>

          {(report.summary.subjects || []).length ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <UserRoundSearch className="h-4 w-4" />
                Matched timeline subjects
              </p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {report.summary.subjects.map((subject) => (
                  <div key={subject.id} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="font-semibold text-slate-900">{subject.full_name}</p>
                    <p className="text-xs text-slate-500">{subject.email || subject.phone_number}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.08em] text-slate-500">{subject.user_type} | {subject.status || 'active'}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Timestamp</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Timeline Focus</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Event</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Actor</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Subject</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>AML / Changes</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Severity</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Amount</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Status</TableHead>
                <TableHead className={REPORT_TABLE_HEAD_CLASS}>Trace</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell className="font-semibold text-slate-900">{row.timeline_label}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.event_type.replaceAll('_', ' ')}</p>
                      <p className="text-xs uppercase tracking-[0.08em] text-slate-500">{row.category}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.actor_name}</p>
                      <p className="text-xs text-slate-500">{row.actor_type}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-slate-900">{row.subject_name}</p>
                      <p className="text-xs text-slate-500">{row.subject_type}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-700">{formatAuditList(row.aml_tags)}</p>
                      <p className="mt-1 text-xs text-slate-500">{row.changed_fields_display}</p>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{row.severity}</TableCell>
                  <TableCell className="font-semibold text-emerald-900">{formatCurrencyOrDash(row.amount)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="capitalize">{row.status}</p>
                      <p className="text-xs text-slate-500">{row.event_hash_short || '-'}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {row.payment_provider_name !== '-' ? (
                        <>
                          <p className="text-xs font-semibold">{row.payment_provider_name}: {row.gateway_reference}</p>
                          {row.gateway_transaction_id !== '-' ? <p className="font-mono text-xs text-slate-500">{row.gateway_transaction_id}</p> : null}
                        </>
                      ) : null}
                      <p className="font-mono text-xs">{row.ip_address}</p>
                      <p className="truncate text-xs text-slate-500">{row.request_path}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <PaginationBar
            pagination={report.pagination}
            loading={reportLoading['activity-timelines']}
            onPageChange={(nextPage) => handlePageChange('activity-timelines', nextPage)}
          />
        </CardContent>
      </Card>
    );
  };

  if (bootLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Activity className="mx-auto mb-4 h-12 w-12 animate-spin text-emerald-900" />
          <p className="text-slate-600">Loading admin reporting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="app-safe-header sticky top-0 z-50 bg-gradient-to-r from-emerald-900 to-emerald-700 text-white shadow-lg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Wallet className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">CaddiePay Admin</h1>
              <p className="text-sm text-emerald-100">Reporting Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden text-right sm:block">
              <p className="text-sm text-emerald-100">Administrator</p>
              <p className="font-semibold">{user?.full_name}</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => loadOverview(overviewFilters)}
              className="rounded-full text-white hover:bg-emerald-800"
              title="Refresh overview"
            >
              <RefreshCw className={`h-5 w-5 ${overviewLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="rounded-full text-white hover:bg-emerald-800"
              title="Profile & Settings"
            >
              <UserCog className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="rounded-full text-white hover:bg-emerald-800"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <PWAInstallBanner />

        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="wallet-card bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-emerald-700">
                <Users className="h-4 w-4" />
                Reporting Coverage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-emerald-900">8</p>
              <p className="mt-2 text-sm text-slate-600">Users, transactions, top-ups, withdrawals, collections, caddie links, audit events, and timelines</p>
            </CardContent>
          </Card>

          <Card className="wallet-card bg-gradient-to-br from-amber-50 to-white border-amber-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-amber-700">
                <ArrowDownToLine className="h-4 w-4" />
                Export Ready
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-amber-900">CSV</p>
              <p className="mt-2 text-sm text-slate-600">Every reporting tab exports the filtered dataset</p>
            </CardContent>
          </Card>

          <Card className="wallet-card bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-blue-700">
                <Activity className="h-4 w-4" />
                Live Filters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-blue-900">Date + Status</p>
              <p className="mt-2 text-sm text-slate-600">Operational filtering across all major financial flows</p>
            </CardContent>
          </Card>

          <Card className="wallet-card bg-gradient-to-br from-slate-100 to-white border-slate-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2 text-slate-700">
                <Shield className="h-4 w-4" />
                Audit Ready
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-slate-900">AML</p>
              <p className="mt-2 text-sm text-slate-600">Immutable audit events and per-user or admin activity timelines are now part of the reporting shell</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="h-auto gap-1 overflow-x-auto border border-slate-200 bg-white p-1 text-slate-700">
            <TabsTrigger value="overview" className="px-4 py-2 font-semibold text-slate-600 data-[state=active]:bg-emerald-900 data-[state=active]:text-white data-[state=inactive]:text-slate-600 hover:text-slate-900">
              Overview
            </TabsTrigger>
            <TabsTrigger value="payment-settings" className="px-4 py-2 font-semibold text-slate-600 data-[state=active]:bg-emerald-900 data-[state=active]:text-white data-[state=inactive]:text-slate-600 hover:text-slate-900">
              Payment Settings
            </TabsTrigger>
            {REPORT_TABS.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="px-4 py-2 font-semibold text-slate-600 data-[state=active]:bg-emerald-900 data-[state=active]:text-white data-[state=inactive]:text-slate-600 hover:text-slate-900"
              >
                {REPORT_LABELS[tab]}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">{renderOverview()}</TabsContent>
          <TabsContent value="payment-settings">{renderPaymentSettings()}</TabsContent>
          <TabsContent value="users">{renderUsersReport()}</TabsContent>
          <TabsContent value="transactions">{renderTransactionsReport()}</TabsContent>
          <TabsContent value="topups">{renderTopupsReport()}</TabsContent>
          <TabsContent value="withdrawals">{renderWithdrawalsReport()}</TabsContent>
          <TabsContent value="collections">{renderCollectionsReport()}</TabsContent>
          <TabsContent value="caddie-links">{renderCaddieLinksReport()}</TabsContent>
          <TabsContent value="audit-events">{renderAuditEventsReport()}</TabsContent>
          <TabsContent value="activity-timelines">{renderActivityTimelinesReport()}</TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

export default AdminDashboard;
